import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Alert,
  Fade,
  Grid,
  Card,
  CardContent,
  useTheme,
  alpha,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Description as DescriptionIcon,
  AccountBalance as AccountBalanceIcon,
  Assessment as AssessmentIcon,
  AttachMoney as AttachMoneyIcon,
  BarChart as BarChartIcon,
  BubbleChart as BubbleChartIcon,
  InsertDriveFile as FileIcon
} from '@mui/icons-material';
import UploadDocumentHandler from '@features/hotels/components/UploadDocumentHandler';
import { detectDocumentType } from '@utils/documentTypeUtils';

// Use the utility function to determine document type based on filename
const detectFileDocumentType = (fileName: string): string => {
  return detectDocumentType(fileName);
};

interface DocumentUploadTabProps {
  error: string | null;
  success: string | null;
  isUploading: boolean;
  hotelId: string;
  onFileUpload: (file: File) => Promise<void>;
  onErrorClose: () => void;
  onSuccessClose: () => void;
}

const DocumentUploadTab: React.FC<DocumentUploadTabProps> = ({
  error,
  success,
  isUploading,
  hotelId,
  onFileUpload,
  onErrorClose,
  onSuccessClose
}) => {
  const theme = useTheme();

  // Handler to detect document type and upload
  const handleFileUploadWithTypeDetection = async (file: File) => {
    try {
      // Use the utility function to detect document type
      const documentType = detectFileDocumentType(file.name);

      // Create a modified file object with document type information
      const fileWithType = new File([file], file.name, { type: file.type });
      // @ts-ignore - Add custom property for document type
      fileWithType.documentType = documentType;

      // Pass the enhanced file to the original onFileUpload function
      await onFileUpload(fileWithType);
    } catch (err) {
      console.error("Error in file upload with type detection:", err);
    }
  };

  return (
    <Box>
      <Typography
        variant="h5"
        gutterBottom
        sx={{
          fontWeight: 600,
          mb: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5
        }}
      >
        <DescriptionIcon fontSize="large" color="primary" />
        Document Management
      </Typography>

      {success && (
        <Fade in={Boolean(success)}>
          <Alert
            severity="success"
            sx={{ mb: 3 }}
            onClose={onSuccessClose}
          >
            {success}
          </Alert>
        </Fade>
      )}

      {error && (
        <Fade in={Boolean(error)}>
          <Alert
            severity="error"
            sx={{ mb: 3 }}
            onClose={onErrorClose}
          >
            {error}
          </Alert>
        </Fade>
      )}

      <Grid container spacing={3}>
        {/* Document Upload Section */}
        <Grid item xs={12} md={7}>
          <Card sx={{ height: '100%', borderRadius: 2, boxShadow: 1 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, display: 'flex', alignItems: 'center' }}>
                <DescriptionIcon sx={{ mr: 1, color: 'primary.main' }} />
                Upload Documents
              </Typography>

              <UploadDocumentHandler
                hotelId={hotelId}
                onSuccess={onSuccessClose}
                onError={onErrorClose}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Document Types Info */}
        <Grid item xs={12} md={5}>
          <Card sx={{ height: '100%', borderRadius: 2, boxShadow: 1 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, display: 'flex', alignItems: 'center' }}>
                <AssessmentIcon sx={{ mr: 1, color: 'primary.main' }} />
                Document Types Supported
              </Typography>

              <List>
                <ListItem>
                  <ListItemIcon>
                    <AccountBalanceIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Financial Documents"
                    secondary="Balance sheets, cash flow statements, income statements, budgets, forecasts"
                  />
                </ListItem>

                <ListItem>
                  <ListItemIcon>
                    <AttachMoneyIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Profit & Loss Statements"
                    secondary="Annual, quarterly, or monthly P&L reports"
                  />
                </ListItem>

                <ListItem>
                  <ListItemIcon>
                    <BarChartIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Performance Metrics"
                    secondary="ADR, RevPAR, and Occupancy metrics files"
                  />
                </ListItem>

                <ListItem>
                  <ListItemIcon>
                    <BubbleChartIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary="STR Reports"
                    secondary="Smith Travel Research competitive analysis reports"
                  />
                </ListItem>

                <ListItem>
                  <ListItemIcon>
                    <FileIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Other Documents"
                    secondary="Any other document types will be categorized accordingly"
                  />
                </ListItem>
              </List>

              <Divider sx={{ my: 2 }} />

              <Typography variant="body2" color="text.secondary">
                File naming is important - include keywords like "financial", "balance sheet", "p&l",
                "adr", "revpar", etc. in your filenames for better automatic categorization.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DocumentUploadTab;
